// Ported from moblyze-clients src/utils/availability.ts (clients#45, #47).
// Keep the decay rules (DECLARATION_TTL_DAYS, FRESH_CONFIRMATION_DAYS and the
// branches of availabilitySignal / matchesAvailability) identical to the
// clients app, so the web portal and the app can never disagree about whether
// a candidate is available.

/**
 * Availability is a decaying declaration, not a fact. This reduces the
 * profile's signals to one displayable state:
 *
 * - "freeFrom": they declared a future date they next come free (e.g. via the
 *   scout-decline sheet) recently enough for it to still mean something.
 * - "unavailable": they said they are not available (or are on assignment),
 *   optionally until a date. Same edge as "available from", read from the
 *   other side, which is why it shares the availableFrom field.
 * - "confirmed": the strongest thing we know is *when they last said* they
 *   were available or looking, surfaced with its age, because a 2-week-old
 *   confirmation and a 14-month-old one are different information.
 * - null: nothing worth showing (the bare actively-looking boolean is ~96%
 *   saturated in prod and carries no signal on its own).
 *
 * Web-only addition: "confirmed" also carries `declared`, true when it comes
 * from a real availability declaration rather than the actively-looking
 * timestamp, so the label can say "Available" vs "Looking". It does not
 * change which signal is returned.
 *
 * `profile` is a CandidateProfile selecting `availability { ... }` and
 * `activelyLookingSetAt` (see CANDIDATE_AVAILABILITY_FRAGMENT).
 */

// A declaration older than this no longer earns a claim about the future.
const DECLARATION_TTL_DAYS = 180;
// A confirmation younger than this renders as fresh (green).
const FRESH_CONFIRMATION_DAYS = 30;

export function availabilitySignal(profile) {
  if (!profile) return null;
  const now = Date.now();

  const declared = profile.availability;
  if (declared) {
    const setAt = new Date(declared.setAt);
    const ageDays = (now - setAt.getTime()) / 86_400_000;
    if (!Number.isNaN(setAt.getTime()) && ageDays <= DECLARATION_TTL_DAYS) {
      const from = declared.availableFrom ? new Date(declared.availableFrom) : null;
      const fromIsFuture = from !== null && from.getTime() > now;
      if (declared.status === "AVAILABLE") {
        if (fromIsFuture) {
          return { kind: "freeFrom", date: from, at: setAt };
        }
        // Available now (or the declared date has passed): the declaration
        // itself is a dated confirmation.
        return {
          kind: "confirmed",
          at: setAt,
          fresh: ageDays <= FRESH_CONFIRMATION_DAYS,
          declared: true,
        };
      }
      return {
        kind: "unavailable",
        status: declared.status,
        until: fromIsFuture ? from : null,
        at: setAt,
      };
    }
  }

  if (profile.activelyLookingSetAt) {
    const at = new Date(profile.activelyLookingSetAt);
    if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return null;
    const ageDays = (now - at.getTime()) / 86_400_000;
    return {
      kind: "confirmed",
      at,
      fresh: ageDays <= FRESH_CONFIRMATION_DAYS,
      declared: false,
    };
  }

  return null;
}

/** "2d" / "3w" / "5mo": coarse age for the confirmed label. */
export function ageLabel(at) {
  const days = Math.max(0, Math.floor((Date.now() - at.getTime()) / 86_400_000));
  if (days < 7) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
}

/** The label for an "unavailable" signal; shared by the row and the line. */
export function unavailableLabel(signal) {
  if (signal.until) {
    const date = formatAvailabilityDate(signal.until);
    return signal.status === "ON_ASSIGNMENT"
      ? `On assignment until ${date}`
      : `Unavailable until ${date}`;
  }
  return signal.status === "ON_ASSIGNMENT" ? "On assignment" : "Not available";
}

/**
 * Which answers a candidate list's availability filter accepts:
 * "any" | "now" | "byJobStart".
 */
export const AVAILABILITY_FILTER_ANY = "any";
export const AVAILABILITY_FILTER_NOW = "now";
export const AVAILABILITY_FILTER_BY_JOB_START = "byJobStart";

/**
 * Does this candidate's latest answer clear the filter?
 *
 * Read through availabilitySignal so the same decay rules apply as to the
 * label: an answer past its TTL stops counting as a yes, and a candidate we
 * have never asked is not a yes either. "byJobStart" accepts someone who
 * comes free on or before the job starts, which is the question a client is
 * actually asking when they look at a pipeline.
 */
export function matchesAvailability(filter, profile, jobStartDate) {
  if (filter === AVAILABILITY_FILTER_ANY) return true;

  const signal = availabilitySignal(profile);
  if (!signal || signal.kind === "unavailable") return false;
  if (signal.kind === "confirmed") {
    // "confirmed" means available now: either no date, or one already passed.
    // A real declaration counts for as long as it has not decayed, but a
    // confirmation that rests only on the bare actively-looking boolean has
    // to be fresh: that flag is ~96% saturated in prod, so an old one is
    // indistinguishable from never having been asked. Ask the signal where it
    // came from, not the profile: an expired declaration is still on the
    // profile, but the signal has already fallen back to the flag.
    if (signal.declared) return true;
    return signal.fresh;
  }
  if (filter === AVAILABILITY_FILTER_NOW) return false;

  const start = jobStartDate ? new Date(jobStartDate) : null;
  if (!start || Number.isNaN(start.getTime())) return false;
  return signal.date.getTime() <= start.getTime();
}

// Web display helpers. The copy is web's own (no em dashes, phrased as a dated
// declaration rather than a promise); the signal underneath is the shared one.

/** "12 Oct" style date, matching the clients app's formatting. */
export function formatAvailabilityDate(date) {
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** "2d ago", or "today" for an age of zero days. */
function agoPhrase(at) {
  const age = ageLabel(at);
  return age === "0d" ? "today" : `${age} ago`;
}

/**
 * The displayable line for a profile, or null when there is nothing worth
 * showing. `text` is the statement ("Free from 12 Oct"), `age` is when the
 * candidate said it ("2d ago", or null when not worth showing), rendered
 * separately and smaller so the statement leads. `tone` is "warning" (a date
 * or a no), "fresh" (a recent yes) or "muted" (an older yes).
 *
 * A stale confirmation that rests only on the actively-looking flag shows no
 * age, as in the clients app: the age is not information anyone can act on
 * and printing it undercuts the only thing the line is there to say.
 */
export function availabilityLabel(profile) {
  const signal = availabilitySignal(profile);
  if (!signal) return null;

  if (signal.kind === "freeFrom") {
    return {
      text: `Free from ${formatAvailabilityDate(signal.date)}`,
      age: agoPhrase(signal.at),
      tone: "warning",
    };
  }
  if (signal.kind === "unavailable") {
    return { text: unavailableLabel(signal), age: agoPhrase(signal.at), tone: "warning" };
  }
  if (signal.declared) {
    return {
      text: "Available",
      age: agoPhrase(signal.at),
      tone: signal.fresh ? "fresh" : "muted",
    };
  }
  return signal.fresh
    ? { text: "Looking", age: agoPhrase(signal.at), tone: "fresh" }
    : { text: "Looking", age: null, tone: "muted" };
}
